import { useEffect, useRef, useState, useCallback } from "react";
import { Camera, CameraOff, LogIn, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { NeedTenant } from "../components/layout/AppShell";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

export default function AttendancePage() {
  const { user, activeTenantId } = useAuth();
  const [time, setTime] = useState(new Date());
  const [records, setRecords] = useState([]);
  const [activeShift, setActiveShift] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraErr, setCameraErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    try {
      const [{ data: recs }, { data: active }] = await Promise.all([
        api.get("/attendance"), api.get("/attendance/active"),
      ]);
      setRecords(recs);
      setActiveShift(active.active);
    } catch (e) { toast.error(fmtErr(e)); }
  }, []);
  useEffect(() => {
    if (user.role === "system_admin" && !activeTenantId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, activeTenantId]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = stream;
      setCameraOn(true);
      setCameraErr(false);
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      });
    } catch {
      setCameraErr(true);
      toast.error("Camera unavailable — a simulated snapshot will be used");
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOn(false);
  };
  useEffect(() => () => stopCamera(), []);

  const capture = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 480; canvas.height = 360;
    const ctx = canvas.getContext("2d");
    if (cameraOn && videoRef.current) {
      ctx.drawImage(videoRef.current, 0, 0, 480, 360);
    } else {
      const grad = ctx.createLinearGradient(0, 0, 480, 360);
      grad.addColorStop(0, "#0F172A"); grad.addColorStop(1, "#334155");
      ctx.fillStyle = grad; ctx.fillRect(0, 0, 480, 360);
      ctx.fillStyle = "#94A3B8"; ctx.font = "bold 28px sans-serif"; ctx.textAlign = "center";
      ctx.fillText(user.name || "Staff", 240, 170);
      ctx.font = "16px monospace";
      ctx.fillText(new Date().toLocaleString(), 240, 205);
    }
    setSelfie(canvas.toDataURL("image/jpeg", 0.7));
  };

  const clockIn = async () => {
    if (!selfie) { toast.error("Capture a selfie first"); return; }
    setBusy(true);
    try {
      await api.post("/attendance/clock-in", { selfie });
      toast.success("Clocked in — have a great shift!");
      setSelfie(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const clockOut = async () => {
    setBusy(true);
    try {
      await api.post("/attendance/clock-out");
      toast.success("Clocked out");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  if (user.role === "system_admin" && !activeTenantId) return <NeedTenant />;

  return (
    <div className="space-y-8" data-testid="attendance-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Attendance</h1>
        <p className="text-sm text-slate-500">Selfie-verified shift clock-in / clock-out</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
          <div className="text-center space-y-1">
            <p className="font-mono font-bold text-4xl tracking-tight text-slate-950" data-testid="attendance-clock">
              {time.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </p>
            <p className="text-sm text-slate-500">{time.toLocaleDateString("en-PH", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>
            {activeShift && (
              <p className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full inline-block px-3 py-1 mt-2">
                On shift since {fmtDateTime(activeShift.time_in)}
              </p>
            )}
          </div>

          <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-slate-900 flex items-center justify-center" data-testid="attendance-camera-preview">
            <video ref={videoRef} autoPlay playsInline muted
              className={`absolute inset-0 object-cover w-full h-full ${selfie || !cameraOn ? "hidden" : ""}`} />
            {selfie && <img src={selfie} alt="selfie" className="absolute inset-0 object-cover w-full h-full" />}
            {!selfie && !cameraOn && (
              <div className="text-center text-slate-400 space-y-2 relative z-10">
                {cameraErr ? <CameraOff className="h-10 w-10 mx-auto" /> : <Camera className="h-10 w-10 mx-auto" />}
                <p className="text-xs">{cameraErr ? "Camera unavailable — simulated capture enabled" : "Camera is off"}</p>
              </div>
            )}
          </div>

          <div className="flex gap-2 justify-center flex-wrap">
            {!cameraOn ? (
              <Button variant="outline" onClick={startCamera} data-testid="attendance-start-camera-btn">
                <Camera className="h-4 w-4 mr-1.5" /> Start camera
              </Button>
            ) : (
              <Button variant="outline" onClick={stopCamera}><CameraOff className="h-4 w-4 mr-1.5" /> Stop</Button>
            )}
            <Button variant="outline" onClick={capture} data-testid="attendance-capture-selfie-btn">
              <Camera className="h-4 w-4 mr-1.5" /> {selfie ? "Retake" : "Capture selfie"}
            </Button>
            {selfie && (
              <Button variant="ghost" onClick={() => setSelfie(null)}><RefreshCw className="h-4 w-4 mr-1.5" /> Clear</Button>
            )}
          </div>

          <div className="flex gap-3">
            {!activeShift ? (
              <Button className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700" onClick={clockIn} disabled={busy || !selfie} data-testid="attendance-clock-in-btn">
                <LogIn className="h-4 w-4 mr-2" /> Clock In
              </Button>
            ) : (
              <Button className="flex-1 h-12 bg-slate-900 hover:bg-slate-800" onClick={clockOut} disabled={busy} data-testid="attendance-clock-out-btn">
                <LogOut className="h-4 w-4 mr-2" /> Clock Out
              </Button>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-slate-900">Attendance Ledger</h3>
          </div>
          <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
            <Table data-testid="attendance-ledger-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Selfie</TableHead>
                  <TableHead>Staff</TableHead>
                  <TableHead>Time In</TableHead>
                  <TableHead>Time Out</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((r) => (
                  <TableRow key={r.attendance_id}>
                    <TableCell>
                      <button onClick={() => setPreview(r.selfie_url)} className="block h-10 w-10 rounded-lg overflow-hidden border border-slate-200" data-testid={`selfie-thumb-${r.attendance_id}`}>
                        <img src={r.selfie_url} alt="" className="object-cover w-full h-full" />
                      </button>
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">{r.user_name}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{fmtDateTime(r.time_in)}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{r.time_out ? fmtDateTime(r.time_out) : "—"}</TableCell>
                    <TableCell>
                      {r.time_out ? (
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">COMPLETED</span>
                      ) : (
                        <span className="rounded bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">ACTIVE SHIFT</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {records.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-sm text-slate-400">No attendance records yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Selfie Verification</DialogTitle></DialogHeader>
          {preview && <img src={preview} alt="selfie proof" className="rounded-lg w-full" data-testid="selfie-preview-image" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
